import { ActionIcon, Group, Modal, NumberInput, Stack, Switch, Table, TextInput } from '@mantine/core';
import { DatePickerInput, DateTimePicker } from '@mantine/dates';
import { FC, Fragment } from 'react';
import type { DataEntryColumnData, DataEntryComponent } from '../../../../api/pages-client/pages-client.type';
import { DataEntryDataType } from '../../../../api/pages-client/pages-client.type';
import { inkLight, primaryBase, redBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { useDataEntryTable } from './data-entry-table.hooks';

interface DataEntryTableProps {
  component: DataEntryComponent;
  onEdit: (component: DataEntryComponent) => void;
  isEditingLayout?: boolean;
}

export const DataEntryTable: FC<DataEntryTableProps> = ({ component, onEdit, isEditingLayout = false }) => {
  const {
    editingRowIndex,
    isAddingRow,
    newRowData,
    editRowData,
    saveError,
    editError,
    deleteConfirmation,
    setNewRowData,
    setEditRowData,
    handleAddRow,
    handleSaveNewRow,
    handleCancelNewRow,
    handleEditRow,
    handleSaveEditRow,
    handleCancelEditRow,
    handleDeleteRow,
    handleConfirmDelete,
    handleCancelDelete,
  } = useDataEntryTable(component, onEdit);

  const renderInputForDataType = (
    column: DataEntryColumnData,
    value: string | number | boolean | null,
    onChange: (value: string | number | boolean | null) => void,
  ) => {
    const inputProps = {
      size: 'sm' as const,
      style: { minWidth: '120px' },
    };

    switch (column.data_type) {
      case DataEntryDataType.TEXT:
        return (
          <TextInput
            {...inputProps}
            value={value as string}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${column.name}`}
          />
        );

      case DataEntryDataType.INTEGER:
        return (
          <NumberInput
            {...inputProps}
            value={value as number}
            onChange={(val) => onChange(val || 0)}
            placeholder={`Enter ${column.name}`}
            allowDecimal={false}
          />
        );

      case DataEntryDataType.FLOAT:
        return (
          <NumberInput
            {...inputProps}
            value={value as number}
            onChange={(val) => onChange(val || 0)}
            placeholder={`Enter ${column.name}`}
            allowDecimal={true}
            decimalScale={2}
          />
        );

      case DataEntryDataType.BOOLEAN:
        return <Switch checked={value as boolean} onChange={(e) => onChange(e.currentTarget.checked)} size="sm" />;

      case DataEntryDataType.DATE:
        return (
          <DatePickerInput
            {...inputProps}
            value={value as string}
            onChange={(date) => onChange(date as string)}
            placeholder={`Select ${column.name}`}
          />
        );

      case DataEntryDataType.DATETIME:
        return (
          <DateTimePicker
            {...inputProps}
            value={value as string}
            onChange={(date) => onChange(date as string)}
            placeholder={`Select ${column.name}`}
          />
        );

      default:
        return (
          <TextInput
            {...inputProps}
            value={value as string}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${column.name}`}
          />
        );
    }
  };

  const renderCellValue = (
    column: { name: string; data_type: DataEntryDataType },
    value: string | number | boolean | null,
  ) => {
    switch (column.data_type) {
      case DataEntryDataType.BOOLEAN:
        return <Icon name={value ? 'check' : 'close'} size={16} color={value ? 'green' : 'red'} />;
      case DataEntryDataType.DATE:
        return <Text size="small">{value as string}</Text>;
      case DataEntryDataType.DATETIME:
        return <Text size="small">{value as string}</Text>;
      default:
        return <Text size="small">{(value as string) ?? '-'}</Text>;
    }
  };

  const hasColumns = component.column_data && component.column_data.length > 0;
  const hasData = component.stored_data && component.stored_data.length > 0;

  if (!hasColumns) {
    return (
      <Stack align="center" gap="md" py="xl">
        <Icon name="table_chart" size={48} color="#ccc" />
        <Text size="small" color="#868e96" style={{ textAlign: 'center' }}>
          No columns configured. Edit the widget to add columns and start entering data.
        </Text>
      </Stack>
    );
  }

  return (
    <>
      <Stack gap="md">
        {!isEditingLayout && (
          <Group justify="space-between">
            <Button
              variant="outline"
              size="sm"
              radius="xl"
              leftSection={<Icon name="add" size={16} />}
              onClick={handleAddRow}
              disabled={isAddingRow || editingRowIndex !== null}
              style={{ width: '100%' }}
            >
              Add Row
            </Button>
          </Group>
        )}
        <div style={{ overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                {component.column_data?.map((column) => (
                  <Table.Th key={column.name}>
                    <Stack gap={2}>
                      <Text size="small" weight="medium">
                        {column.name}
                      </Text>
                      <Text size="tiny" color="#868e96">
                        {column.data_type}
                      </Text>
                    </Stack>
                  </Table.Th>
                ))}
                {!isEditingLayout && <Table.Th style={{ width: 100 }}>Actions</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {/* Add new row */}
              {isAddingRow && (
                <Fragment key="new-row">
                  <Table.Tr style={{ backgroundColor: 'rgba(29, 117, 222, 0.05)' }}>
                    {component.column_data?.map((column) => (
                      <Table.Td key={column.name}>
                        {renderInputForDataType(column, newRowData[column.name], (value) =>
                          setNewRowData({ ...newRowData, [column.name]: value }),
                        )}
                      </Table.Td>
                    ))}
                    {!isEditingLayout && (
                      <Table.Td>
                        <Group gap={4}>
                          <Tooltip label="Save" variant="black">
                            <ActionIcon variant="subtle" size="sm" color="green" onClick={handleSaveNewRow}>
                              <Icon name="check" size={14} style={{ cursor: 'pointer' }} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Cancel" variant="black">
                            <ActionIcon variant="subtle" size="sm" color="gray" onClick={handleCancelNewRow}>
                              <Icon name="close" size={14} style={{ cursor: 'pointer' }} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    )}
                  </Table.Tr>
                  {saveError && (
                    <Table.Tr key="save-error">
                      <Table.Td colSpan={(component.column_data?.length || 0) + (!isEditingLayout ? 1 : 0)}>
                        <Text size="small" color={redBase} weight="medium" style={{ padding: '8px 0' }}>
                          {saveError}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Fragment>
              )}

              {/* Existing rows */}
              {hasData &&
                component.stored_data.map((row, rowIndex) => (
                  <Fragment key={`row-${rowIndex}`}>
                    <Table.Tr>
                      {component.column_data?.map((column) => (
                        <Table.Td key={column.name}>
                          {editingRowIndex === rowIndex
                            ? renderInputForDataType(column, editRowData[column.name] ?? row[column.name], (value) =>
                                setEditRowData({ ...editRowData, [column.name]: value }),
                              )
                            : renderCellValue(column, row[column.name])}
                        </Table.Td>
                      ))}
                      {!isEditingLayout && (
                        <Table.Td>
                          {editingRowIndex === rowIndex ? (
                            <Group gap={4}>
                              <Tooltip label="Save" variant="black">
                                <ActionIcon variant="subtle" size="sm" color="green" onClick={handleSaveEditRow}>
                                  <Icon name="check" size={14} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Cancel" variant="black">
                                <ActionIcon variant="subtle" size="sm" color="gray" onClick={handleCancelEditRow}>
                                  <Icon name="close" size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          ) : (
                            <Group gap={4}>
                              <Tooltip label="Edit" variant="black">
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  color={primaryBase}
                                  onClick={() => handleEditRow(rowIndex)}
                                  disabled={isAddingRow || editingRowIndex !== null}
                                >
                                  <Icon name="edit" size={14} style={{ cursor: 'pointer' }} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Delete" variant="black">
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  color="red"
                                  onClick={() => handleDeleteRow(rowIndex)}
                                  disabled={isAddingRow || editingRowIndex !== null}
                                >
                                  <Icon name="delete" size={14} style={{ cursor: 'pointer' }} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          )}
                        </Table.Td>
                      )}
                    </Table.Tr>
                    {editingRowIndex === rowIndex && editError && (
                      <Table.Tr key={`edit-error-${rowIndex}`}>
                        <Table.Td colSpan={(component.column_data?.length || 0) + (!isEditingLayout ? 1 : 0)}>
                          <Text size="small" color={redBase} weight="medium" style={{ padding: '8px 0' }}>
                            {editError}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Fragment>
                ))}

              {/* Empty state when no data */}
              {!hasData && !isAddingRow && (
                <Table.Tr key="empty-state">
                  <Table.Td colSpan={(component.column_data?.length || 0) + (!isEditingLayout ? 1 : 0)}>
                    <Stack align="center" gap="sm" py="md">
                      <Icon name="inbox" size={32} color="#ccc" />
                      <Text size="small" color="#868e96" style={{ textAlign: 'center' }}>
                        No data entries yet. Click "Add Row" to start adding data.
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </div>
      </Stack>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteConfirmation.isOpen}
        onClose={handleCancelDelete}
        centered
        radius="lg"
        size="sm"
        padding="24px"
        withCloseButton={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Icon
            name="close"
            size={20}
            color={skyDark}
            style={{ position: 'absolute', top: 12, right: 12, cursor: 'pointer' }}
            onClick={handleCancelDelete}
          />
          <Text size="medium" weight="bold" style={{ marginBottom: 12 }}>
            Delete Row
          </Text>
          <Text style={{ marginBottom: 8 }}>Are you sure you want to delete this row?</Text>
          <Text color={redBase} weight="medium" style={{ marginBottom: 20 }}>
            This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelDelete}
              radius="xl"
              leftSection={<Icon name="cancel" size={14} color={inkLight} />}
            >
              <Text size="small" weight="medium" style={{ color: inkLight }}>
                Cancel
              </Text>
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmDelete}
              radius="xl"
              style={{ backgroundColor: redBase }}
              leftSection={<Icon name="delete" size={14} color="white" />}
            >
              <Text size="small" weight="medium" style={{ color: 'white' }}>
                Delete Row
              </Text>
            </Button>
          </Group>
        </div>
      </Modal>
    </>
  );
};
